class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.dismissedSearchTerm = null;
    this.resultsSearchTerm = null;
    this.resultsId = `${this.input.id || 'PredictiveSearch'}-Results`;
    this.input.setAttribute('aria-controls', this.resultsId);
    this.input.setAttribute('aria-owns', this.resultsId);

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('[id$="predictive-search-results-groups-wrapper"]')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    if (!this.contains(document.activeElement) || this.dismissedSearchTerm === this.searchTerm) return;
    this.dismissedSearchTerm = null;
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;
    this.dismissedSearchTerm = null;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true' && this.resultsSearchTerm === currentSearchTerm) {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        event.preventDefault();
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    } else if (event.code === 'Escape') {
      event.preventDefault();
      this.input.focus();
      this.close();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm) {
      if (currentButtonText.split(previousTerm).length !== 2) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, () => newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    if (!allVisibleElements.length) {
      this.input.setAttribute('aria-activedescendant', '');
      return;
    }
    const selectedElementIndex = allVisibleElements.indexOf(selectedElement);
    if (moveUp && selectedElementIndex === -1) return;
    const activeElementIndex = selectedElementIndex === -1
      ? 0
      : (selectedElementIndex + (moveUp ? -1 : 1) + allVisibleElements.length) % allVisibleElements.length;
    if (activeElementIndex === selectedElementIndex) return;

    this.statusElement.textContent = '';
    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    if (this.dismissedSearchTerm === searchTerm || !this.contains(document.activeElement)) return;
    this.abortController.abort();
    this.abortController = new AbortController();
    const abortController = this.abortController;
    const isCurrentRequest = () => this.abortController === abortController && !abortController.signal.aborted &&
      this.searchTerm === searchTerm && this.dismissedSearchTerm !== searchTerm && this.contains(document.activeElement);
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      if (this.searchTerm === searchTerm) this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const searchParams = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product,collection,page,article',
      'resources[limit]': '6',
      section_id: 'predictive-search',
    });

    fetch(`${routes.predictive_search_url}?${searchParams.toString()}`, {
      signal: abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (!isCurrentRequest()) return;
        const responseSection = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');

        if (!responseSection) throw new Error('Predictive search response did not include the expected section.');

        const resultsMarkup = responseSection.innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        if (this.searchTerm === searchTerm) this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20 || !isCurrentRequest()) return;
        this.renderErrorState();
      });
  }

  renderErrorState() {
    const results = document.createElement('div');
    results.id = this.resultsId;
    results.className = 'predictive-search__results-groups-wrapper';
    results.setAttribute('role', 'status');

    const message = document.createElement('p');
    message.className = 'predictive-search__item query-suggestion';
    message.textContent = 'Search suggestions are temporarily unavailable. Press Enter to search.';
    results.appendChild(message);

    this.predictiveSearchResults.replaceChildren(results);
    this.resultsSearchTerm = null;
    this.setAttribute('results', true);
    this.removeAttribute('loading');
    this.setLiveRegionText(message.textContent);
    this.open();
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    const resultsContainer = document.createElement('div');
    resultsContainer.innerHTML = resultsMarkup;

    const idMap = new Map();
    resultsContainer.querySelectorAll('[id]').forEach((element) => {
      const currentId = element.id;
      const uniqueId =
        currentId === 'predictive-search-results' ? this.resultsId : `${this.resultsId}-${currentId}`;
      idMap.set(currentId, uniqueId);
      element.id = uniqueId;
    });

    ['aria-controls', 'aria-describedby', 'aria-labelledby', 'aria-owns'].forEach((attribute) => {
      resultsContainer.querySelectorAll(`[${attribute}]`).forEach((element) => {
        const updatedIds = element
          .getAttribute(attribute)
          .split(/\s+/)
          .map((id) => idMap.get(id) || id)
          .join(' ');
        element.setAttribute(attribute, updatedIds);
      });
    });

    this.predictiveSearchResults.innerHTML = resultsContainer.innerHTML;
    this.resultsSearchTerm = this.searchTerm;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    this.abortController.abort();
    this.dismissedSearchTerm = this.getQuery();
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
